import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft, X, Sparkles, MapPin, Calendar, Users, Mic, Camera, ShoppingBag, Heart, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TOUR_STEPS = [
  {
    path: '/dashboard',
    target: '[data-testid="action-cards"]',
    title: 'Your Dashboard',
    content: 'Your home base — quick-tap actions to find events, request rides, offer lifts, or create your own event.',
    icon: LayoutDashboard,
    color: '#a78bfa',
  },
  {
    path: '/events',
    target: '[data-testid="event-search-input"]',
    fallbackTarget: '.min-h-screen',
    title: 'Events',
    content: 'Everything happening in the UK skating scene. Search, filter by date, and book rides to get there.',
    icon: Calendar,
    color: '#f97316',
  },
  {
    path: '/rollflix',
    target: '[data-testid="rollflix-hero"]',
    fallbackTarget: '.min-h-screen',
    title: 'Community',
    content: 'The social hub — follow corners, discover DJs, organisers and skaters from across the scene.',
    icon: Users,
    color: '#3b82f6',
  },
  {
    path: '/ranting-spot',
    target: '.min-h-screen',
    title: 'Unfiltered',
    content: 'Got something to say? Drop a 60-second video rant and share it with the crew. Rants vanish after 30 days!',
    icon: Mic,
    color: '#ef4444',
  },
  {
    path: '/floor-tax',
    target: '.min-h-screen',
    title: 'Floor Tax',
    content: 'Caught someone stacking it? Post a 30-second clip. Videos disappear after 30 days — catch them while you can.',
    icon: Camera,
    color: '#eab308',
  },
  {
    path: '/sk8-shop',
    target: '.min-h-screen',
    title: 'Community Listings',
    content: 'Buy, sell, or swap skating gear with the community. List your old wheels or find your next setup.',
    icon: ShoppingBag,
    color: '#10b981',
  },
  {
    path: '/safe-space',
    target: '[data-testid="safe-space-page"]',
    fallbackTarget: '.min-h-screen',
    title: 'Safe Space',
    content: 'A supportive, anonymous space for the community. Share your thoughts freely — everyone is welcome.',
    icon: Heart,
    color: '#ec4899',
  },
  {
    path: '/dashboard',
    target: '[data-testid="action-cards"]',
    title: "You're All Set!",
    content: "That's the tour! Explore at your own pace. You can replay this anytime from the Menu.",
    icon: Sparkles,
    color: '#a78bfa',
  },
];

/* ─── Global keyframe styles injected once ─── */
const TourStyles = () => (
  <style>{`
    @keyframes tourFadeSlideIn {
      from { opacity: 0; transform: translateY(10px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes tourArrowBounce {
      0%, 100% { transform: translateY(0); }
      50%      { transform: translateY(-10px); }
    }
    @keyframes tourArrowBounceDown {
      0%, 100% { transform: translateY(0) rotate(180deg); }
      50%      { transform: translateY(10px) rotate(180deg); }
    }
    @keyframes tourPulseRing {
      0%   { box-shadow: 0 0 0 0 hsla(var(--primary), 0.4); }
      70%  { box-shadow: 0 0 0 12px hsla(var(--primary), 0); }
      100% { box-shadow: 0 0 0 0 hsla(var(--primary), 0); }
    }
    @keyframes tourSpotPulse {
      0%, 100% { opacity: 0.6; }
      50%      { opacity: 1; }
    }
    @keyframes tourIconSpin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes tourDotPop {
      0%   { transform: scale(0); opacity: 0; }
      60%  { transform: scale(1.3); }
      100% { transform: scale(1); opacity: 1; }
    }
  `}</style>
);

/* ─── Spotlight overlay with pulsing border ─── */
const Spotlight = ({ rect, color }) => {
  if (!rect) return null;
  const pad = 12;
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998, pointerEvents: 'none' }}>
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - pad} y={rect.top - pad}
                width={rect.width + pad * 2} height={rect.height + pad * 2}
                rx="16" fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#tour-mask)" />
        </svg>
      </div>
      {/* Pulsing highlight border */}
      <div style={{
        position: 'fixed',
        left: rect.left - pad, top: rect.top - pad,
        width: rect.width + pad * 2, height: rect.height + pad * 2,
        borderRadius: 16,
        border: `2px solid ${color || 'hsl(var(--primary))'}`,
        boxShadow: `0 0 0 4px ${color}33, 0 0 30px ${color}22`,
        zIndex: 9999, pointerEvents: 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'tourSpotPulse 2s ease-in-out infinite',
      }} />
    </>
  );
};

/* ─── Tooltip card with built-in animated arrow ─── */
const Tooltip = ({ rect, step, stepIndex, totalSteps, onNext, onPrev, onSkip }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  // Compute position synchronously during render — no async timing issues
  const layout = (() => {
    if (!rect) return { top: 100, left: 100, place: 'below', arrowPct: 50 };
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = document.documentElement.clientHeight || window.innerHeight;
    const isMobile = vw < 500;
    const tooltipH = isMobile ? 210 : 230;
    const tooltipW = isMobile ? Math.min(vw - 24, 340) : 370;
    const arrowH = 30;
    const gap = 14;

    const rectBottom = rect.top + rect.height;
    const spaceBelow = vh - rectBottom;
    const spaceAbove = rect.top;

    let top, place;

    if (spaceBelow >= tooltipH + gap + arrowH) {
      top = rectBottom + gap + arrowH;
      place = 'below';
    } else if (spaceAbove >= tooltipH + gap + arrowH) {
      top = rect.top - tooltipH - gap - arrowH;
      place = 'above';
    } else {
      // Doesn't fit cleanly — put at bottom with arrow pointing up
      top = vh - tooltipH - 10;
      place = 'below';
    }

    top = Math.max(arrowH + 2, top);
    top = Math.min(top, vh - tooltipH - 10);

    let left = rect.left + rect.width / 2 - tooltipW / 2;
    left = Math.max(12, Math.min(left, vw - tooltipW - 12));

    const targetCenterX = rect.left + rect.width / 2;
    const arrowPct = Math.max(12, Math.min(88, ((targetCenterX - left) / tooltipW) * 100));

    return { top, left, place, arrowPct, tooltipW };
  })();

  if (!rect) return null;

  const StepIcon = step.icon;

  return (
    <div
      data-testid="tour-tooltip"
      style={{
        position: 'fixed', top: layout.top, left: layout.left,
        zIndex: 10000, width: layout.tooltipW, maxWidth: 'calc(100vw - 24px)',
        overflow: 'visible',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.97)',
        transition: 'opacity 0.35s ease-out, transform 0.35s ease-out',
      }}
    >
      {/* Animated glowing arrow */}
      <div
        data-testid="tour-arrow"
        style={{
          position: 'absolute',
          left: `${layout.arrowPct}%`,
          ...(layout.place === 'below'
            ? { top: -28, transform: 'translateX(-50%)' }
            : { bottom: -28, transform: 'translateX(-50%) rotate(180deg)' }
          ),
          zIndex: 10001,
          pointerEvents: 'none',
          animation: layout.place === 'below'
            ? 'tourArrowBounce 1s ease-in-out infinite'
            : 'tourArrowBounceDown 1s ease-in-out infinite',
        }}
      >
        <svg width="48" height="30" viewBox="0 0 48 30" fill="none" style={{ filter: `drop-shadow(0 0 12px ${step.color}) drop-shadow(0 0 4px ${step.color})` }}>
          <path
            d="M24 2 L6 28 L42 28 Z"
            fill={step.color}
            stroke="white"
            strokeWidth="1.5"
            opacity="0.95"
          />
        </svg>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        {/* Colored accent bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${step.color}, ${step.color}88)` }} />

        <div className="p-5">
          {/* Header row: icon + title + close */}
          <div className="flex items-center gap-3 mb-3">
            <div
              className="flex items-center justify-center rounded-xl"
              style={{
                width: 40, height: 40,
                background: `${step.color}18`,
                border: `1.5px solid ${step.color}44`,
              }}
            >
              {StepIcon && <StepIcon style={{ width: 20, height: 20, color: step.color }} />}
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold leading-tight">{step.title}</h3>
              <span className="text-xs text-muted-foreground">{stepIndex + 1} of {totalSteps}</span>
            </div>
            <button
              onClick={onSkip}
              className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              data-testid="tour-skip-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed mb-4">{step.content}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === stepIndex ? 20 : 6,
                  height: 6,
                  background: i === stepIndex ? step.color : i < stepIndex ? `${step.color}66` : 'hsl(var(--border))',
                  animation: i === stepIndex ? 'tourDotPop 0.3s ease-out' : 'none',
                }}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <div>
              {stepIndex > 0 ? (
                <Button
                  variant="ghost" size="sm"
                  className="rounded-full h-9 px-4 text-sm"
                  onClick={onPrev}
                  data-testid="tour-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              ) : (
                <Button
                  variant="ghost" size="sm"
                  className="rounded-full h-9 px-4 text-xs text-muted-foreground"
                  onClick={onSkip}
                  data-testid="tour-skip-all-btn"
                >
                  Skip Tour
                </Button>
              )}
            </div>
            <Button
              size="sm"
              className="rounded-full h-9 px-5 text-sm font-semibold"
              style={{ background: step.color, color: '#fff' }}
              onClick={onNext}
              data-testid="tour-next-btn"
            >
              {stepIndex === totalSteps - 1 ? (
                <>Let's Roll! <Sparkles className="w-3.5 h-3.5 ml-1" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-0.5" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Loading state between page navigations ─── */
const TourLoader = () => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 9997, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div className="flex flex-col items-center gap-3">
      <div style={{ animation: 'tourIconSpin 1.2s linear infinite' }}>
        <Sparkles className="w-8 h-8 text-primary" />
      </div>
      <span className="text-sm text-muted-foreground font-medium">Loading next stop...</span>
    </div>
  </div>
);

/* ─── Main GuidedTour component ─── */
const GuidedTour = ({ run, onFinish }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [active, setActive] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const finish = useCallback(() => {
    setActive(false);
    setStepIndex(0);
    setTargetRect(null);
    localStorage.setItem('rollm8s_guided_tour_complete', 'true');
    if (onFinish) onFinish();
  }, [onFinish]);

  // Start tour
  useEffect(() => {
    if (run && !active) {
      setStepIndex(0);
      setActive(true);
      setNavigating(false);
    }
  }, [run]);

  // Navigate to correct page for current step
  useEffect(() => {
    if (!active) return;
    const step = TOUR_STEPS[stepIndex];
    if (!step) { finish(); return; }

    if (location.pathname !== step.path) {
      setNavigating(true);
      setTargetRect(null);
      navigate(step.path);
    }
  }, [active, stepIndex]);

  // Find target element after navigation settles
  useEffect(() => {
    if (!active) return;
    const step = TOUR_STEPS[stepIndex];
    if (!step) return;

    const delay = navigating ? 1200 : 300;

    const timer = setTimeout(() => {
      setNavigating(false);
      const el = document.querySelector(step.target) || document.querySelector(step.fallbackTarget || '.min-h-screen');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          // Keep spotlight compact so tooltip + arrow always fit on screen
          const vh = document.documentElement.clientHeight || window.innerHeight;
          const maxH = vh * 0.45;
          setTargetRect({
            left: rect.left,
            top: Math.max(0, rect.top),
            width: rect.width,
            height: Math.min(rect.height, maxH),
          });
        }, 400);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [active, stepIndex, location.pathname, navigating]);

  // Recalculate on scroll/resize
  useEffect(() => {
    if (!active || navigating) return;
    const step = TOUR_STEPS[stepIndex];
    if (!step) return;

    const handleUpdate = () => {
      const el = document.querySelector(step.target) || document.querySelector(step.fallbackTarget || '.min-h-screen');
      if (el) {
        const rect = el.getBoundingClientRect();
        const vh = document.documentElement.clientHeight || window.innerHeight;
        const maxH = vh * 0.45;
        setTargetRect({
          left: rect.left, top: Math.max(0, rect.top),
          width: rect.width, height: Math.min(rect.height, maxH),
        });
      }
    };
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [active, stepIndex, navigating]);

  // Keyboard controls
  useEffect(() => {
    if (!active) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [active, stepIndex]);

  const handleNext = () => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      setStepIndex(s => s + 1);
    } else {
      finish();
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) setStepIndex(s => s - 1);
  };

  if (!active) return null;

  if (navigating || !targetRect) {
    return createPortal(<><TourStyles /><TourLoader /></>, document.body);
  }

  const step = TOUR_STEPS[stepIndex];

  return createPortal(
    <>
      <TourStyles />
      <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} onClick={(e) => e.stopPropagation()} />
      <Spotlight rect={targetRect} color={step.color} />
      <Tooltip
        rect={targetRect}
        step={step}
        stepIndex={stepIndex}
        totalSteps={TOUR_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={finish}
      />
    </>,
    document.body
  );
};

export default GuidedTour;
